import React, { useEffect, useMemo, useRef, useState } from "react";

export function Carousel({
  items = [],
  renderItem,
  className = "",
  itemClassName = "",
  heightClass = "h-64",
  autoPlay = false,
  intervalMs = 4000,
  showArrows = true,
  showIndicators = true,
  loop = true,
  startingIndex = 0,
  onIndexChange,
  onAddClick, // ⬅️ 추가: __add 슬라이드 클릭 시 실행할 콜백
  ariaLabel = "Carousel",
}) {
  const normalizedItems = Array.isArray(items) ? items : items ? [items] : [];

  const [index, setIndex] = useState(() =>
    Math.min(
      Math.max(startingIndex, 0),
      Math.max(normalizedItems.length - 1, 0)
    )
  );
  const containerRef = useRef(null);
  const pointerRef = useRef({ active: false, startX: 0, lastX: 0, deltaX: 0 });
  const autoRef = useRef(null);

  const count = normalizedItems.length;

  const clampIndex = (i) => {
    if (loop && count > 0) return (i + count) % count;
    return Math.min(Math.max(i, 0), Math.max(count - 1, 0));
  };

  const goTo = (i) => {
    const next = clampIndex(i);
    setIndex(next);
    onIndexChange?.(next);
  };

  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // autoplay
  useEffect(() => {
    if (!autoPlay || count <= 1) return;
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => next(), Math.max(1500, intervalMs));
    return () => clearInterval(autoRef.current);
  }, [autoPlay, intervalMs, index, count]);

  // keyboard
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
  };

  // ⬇️ 추가: 내부 인터랙티브 요소(화살표/업로드 버튼 등)면 스와이프 무시
  const isInteractiveTarget = (el) =>
    typeof el?.closest === "function" &&
    el.closest("[data-carousel-interactive]");

  // swipe handlers
  const onPointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return; // ⬅️ 추가
    if (!containerRef.current) return;
    containerRef.current.setPointerCapture?.(e.pointerId);
    pointerRef.current = {
      active: true,
      startX: e.clientX,
      lastX: e.clientX,
      deltaX: 0,
    };
  };

  const onPointerMove = (e) => {
    if (!pointerRef.current.active) return;
    const dx = e.clientX - pointerRef.current.startX;
    pointerRef.current.lastX = e.clientX;
    pointerRef.current.deltaX = dx;

    const track = containerRef.current?.querySelector("[data-carousel-track]");
    if (track && containerRef.current) {
      track.style.transition = "none";
      const base = -index * 100;
      const pct = (dx / containerRef.current.clientWidth) * 100;
      track.style.transform = `translateX(${base + pct}%)`;
    }
  };

  const endDrag = (e) => {
    if (!pointerRef.current.active) return;
    pointerRef.current.active = false;

    const { deltaX } = pointerRef.current;
    const threshold = (containerRef.current?.clientWidth || 1) * 0.15; // 15%
    const track = containerRef.current?.querySelector("[data-carousel-track]");
    if (track) track.style.transition = "transform 300ms ease";

    if (Math.abs(deltaX) > threshold) {
      deltaX < 0 ? next() : prev();
    } else {
      if (track) track.style.transform = `translateX(${-index * 100}%)`;
    }
  };

  // sync transform when index changes
  useEffect(() => {
    const track = containerRef.current?.querySelector("[data-carousel-track]");
    if (track) {
      track.style.transition = "transform 300ms ease";
      track.style.transform = `translateX(${-index * 100}%)`;
    }
  }, [index]);

  // 기본 슬라이드 렌더러
  const DefaultSlide = (item, i) => {
    // ⬇️ __add 슬라이드 지원: renderItem 미제공 시 자동 + 버튼
    if (item?.__add) {
      return (
        <button
          type="button"
          onClick={onAddClick}
          onPointerDown={(e) => e.stopPropagation()}
          data-carousel-interactive
          className="h-full w-full grid place-items-center bg-slate-800/60 text-white/85"
        >
          <span className="text-6xl leading-none select-none">＋</span>
          <span className="mt-2 text-sm select-none">이미지 추가</span>
        </button>
      );
    }

    const url = typeof item === "string" ? item : item?.url || item?.src || ""; // ⬅️ url/src 둘 다 지원
    return url ? (
      <img
        src={url}
        alt={item?.alt ?? `slide-${i + 1}`}
        className="h-full w-full object-contain select-none"
        draggable={false}
      />
    ) : (
      <div className="w-full h-full bg-slate-100" />
    );
  };

  const Indicators = useMemo(() => {
    if (!showIndicators || count <= 1) return null;
    return (
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => goTo(i)}
            data-carousel-interactive // ⬅️ 인터랙티브로 마킹
            className={[
              "h-2 w-2 rounded-full transition-all",
              i === index ? "w-6 bg-white/90" : "bg-white/50 hover:bg-white/70",
              "ring-1 ring-black/10",
            ].join(" ")}
          />
        ))}
      </div>
    );
  }, [showIndicators, count, index]);

  const Arrows = useMemo(() => {
    if (!showArrows || count <= 1) return null;
    return (
      <>
        <button
          type="button"
          aria-label="Previous"
          onClick={prev}
          onPointerDown={(e) => e.stopPropagation()} // ⬅️ 스와이프와 충돌 방지
          data-carousel-interactive // ⬅️ 인터랙티브로 마킹
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 hover:bg-white shadow p-2 ring-1 ring-black/10 backdrop-blur"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-5 w-5"
          >
            <path
              fillRule="evenodd"
              d="M15.78 4.22a.75.75 0 010 1.06L9.06 12l6.72 6.72a.75.75 0 11-1.06 1.06l-7.25-7.25a.75.75 0 010-1.06l7.25-7.25a.75.75 0 011.06 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={next}
          onPointerDown={(e) => e.stopPropagation()} // ⬅️ 스와이프와 충돌 방지
          data-carousel-interactive // ⬅️ 인터랙티브로 마킹
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 hover:bg-white shadow p-2 ring-1 ring-black/10 backdrop-blur"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-5 w-5"
          >
            <path
              fillRule="evenodd"
              d="M8.22 19.78a.75.75 0 010-1.06L14.94 12 8.22 5.28a.75.75 0 111.06-1.06l7.25 7.25a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </>
    );
  }, [showArrows, count, index]);

  return (
    <section
      ref={containerRef}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={[
        "relative overflow-hidden select-none",
        "rounded-2xl shadow-sm ring-1 ring-black/5 bg-white",
        heightClass,
        className,
      ].join(" ")}
    >
      {/* Track */}
      <div
        data-carousel-track
        className="flex h-full w-full will-change-transform"
        style={{ transform: `translateX(${-index * 100}%)` }}
      >
        {normalizedItems.map((item, i) => (
          <div
            key={i}
            className={["w-full flex-none h-full", itemClassName].join(" ")}
          >
            {renderItem ? renderItem(item, i) : DefaultSlide(item, i)}
          </div>
        ))}
      </div>

      {Arrows}
      {Indicators}
    </section>
  );
}

export default Carousel;
